import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, User as AuthUser } from '../context/AuthContext';
import { 
  requestLoginCode, 
  verifyLoginCode,
  requestSmsOtp,
  verifySmsOtp,
  checkTotpEnabled,
  loginWithTotp,
  getDevSettings,
  devLogin
} from '../lib/api';
import { 
  Envelope, 
  ArrowRight, 
  Globe, 
  Spinner, 
  Phone, 
  ShieldCheck,
  CaretDown,
  Code,
  Crown,
  Shield,
  Users,
  User,
  Truck,
  House,
  Scales,
  UserPlus
} from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';

const AUTH_METHODS: Record<string, { id: string; label: string; labelEn: string; icon: typeof Envelope; description: string; descriptionEn: string }> = {
  email: {
    id: 'email',
    label: 'E-post',
    labelEn: 'Email',
    icon: Envelope,
    description: 'Få en engångskod via e-post',
    descriptionEn: 'Get a one-time code via email'
  },
  sms: {
    id: 'sms',
    label: 'SMS',
    labelEn: 'SMS',
    icon: Phone,
    description: 'Få en engångskod via SMS till din telefon',
    descriptionEn: 'Get a one-time code via SMS to your phone'
  },
  totp: {
    id: 'totp',
    label: 'Autentiseringsapp',
    labelEn: 'Authenticator App',
    icon: ShieldCheck,
    description: 'Använd Google Authenticator eller liknande',
    descriptionEn: 'Use Google Authenticator or similar'
  }
};

// Dev login roles with icons and colors
const DEV_ROLES = [
  { role: 'superadmin', label: 'Superadmin', icon: Crown, color: 'bg-purple-500 hover:bg-purple-600' },
  { role: 'admin', label: 'Admin', icon: Shield, color: 'bg-red-500 hover:bg-red-600' },
  { role: 'auditor', label: 'Revisor', icon: Scales, color: 'bg-indigo-500 hover:bg-indigo-600' },
  { role: 'suppliant', label: 'Suppleant', icon: UserPlus, color: 'bg-teal-500 hover:bg-teal-600' },
  { role: 'board_member', label: 'Styrelsemedlem', icon: Users, color: 'bg-amber-500 hover:bg-amber-600' },
  { role: 'resident', label: 'Boende', icon: User, color: 'bg-slate-500 hover:bg-slate-600' },
  { role: 'subcontractor', label: 'Leverantör', icon: Truck, color: 'bg-green-500 hover:bg-green-600' },
  { role: 'external', label: 'Extern', icon: House, color: 'bg-sky-500 hover:bg-sky-600' }
];

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState('method'); // method | input | code
  const [authMethod, setAuthMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  
  // Dev login state
  const [devLoginEnabled, setDevLoginEnabled] = useState(false);
  const [devLoading, setDevLoading] = useState<string | null>(null);
  
  // Ref to prevent double verification calls
  const verifyInProgress = useRef(false);
  const verifyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if dev login is enabled
  useEffect(() => {
    const checkDevLogin = async () => {
      try {
        const settings = await getDevSettings();
        setDevLoginEnabled(settings.dev_login_enabled === true);
      } catch (error) {
        console.error('Failed to check dev settings:', error);
      }
    };
    checkDevLogin();
  }, []);

  // Load saved auth method preference
  useEffect(() => {
    const savedMethod = localStorage.getItem('preferredAuthMethod');
    if (savedMethod && AUTH_METHODS[savedMethod]) {
      setAuthMethod(savedMethod);
    }
  }, []);

  const toggleLanguage = (): void => {
    const newLang = i18n.language === 'sv' ? 'en' : 'sv';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  // Handle dev login
  const handleDevLogin = async (role: string): Promise<void> => {
    setDevLoading(role);
    try {
      const { user, token } = await devLogin(role);
      // Ensure user has required email for AuthContext
      const authUser: AuthUser = {
        ...user,
        id: user.id || '',
        email: user.email || '',
        role: user.role || role
      };
      login(token, authUser);
      toast.success(`DEV-inloggad som ${role}`);
      
      if (role === 'superadmin') {
        navigate('/superadmin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Dev login failed:', error);
      toast.error('DEV-inloggning misslyckades');
    } finally {
      setDevLoading(null);
    }
  };

  const selectMethod = (method: string): void => {
    setAuthMethod(method);
    localStorage.setItem('preferredAuthMethod', method);
    setShowMethodPicker(false);
    setStep('input');
    // Reset fields
    setEmail('');
    setPhone('');
    setCode('');
  };

  const formatPhone = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as Swedish phone number
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const handleRequestCode = async (e: React.FormEvent | React.MouseEvent): Promise<void> => {
    e.preventDefault();
    
    if (authMethod === 'email' && !email) return;
    if (authMethod === 'sms' && !phone) return;
    if (authMethod === 'totp' && !email) return;
    
    setLoading(true);
    try {
      if (authMethod === 'email') {
        await requestLoginCode(email);
        toast.success(i18n.language === 'sv' ? 'En kod har skickats till din e-post' : 'A code has been sent to your email');
        setStep('code');
      } else if (authMethod === 'sms') {
        await requestSmsOtp(phone);
        toast.success(i18n.language === 'sv' ? 'En kod har skickats via SMS' : 'A code has been sent via SMS');
        setStep('code');
      } else if (authMethod === 'totp') {
        // Check if user has TOTP enabled
        const totpStatus = await checkTotpEnabled(email);
        if (totpStatus.totp_enabled) {
          setStep('code');
        } else {
          toast.error(i18n.language === 'sv' 
            ? 'Autentiseringsapp är inte aktiverad för detta konto. Logga in med e-post eller SMS först och aktivera det i inställningar.' 
            : 'Authenticator app is not enabled for this account. Log in with email or SMS first and enable it in settings.');
        }
      }
    } catch (error) {
      console.error('Request code error:', error);
      if (authMethod === 'sms') {
        toast.error(i18n.language === 'sv' ? 'Kunde inte skicka SMS. Kontrollera telefonnumret.' : 'Could not send SMS. Check the phone number.');
      } else if (authMethod === 'totp') {
        toast.error(i18n.language === 'sv' ? 'Kunde inte kontrollera TOTP-status. Kontrollera e-postadressen.' : 'Could not check TOTP status. Check the email address.');
      } else {
        toast.error(t('error_occurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (value: string): Promise<void> => {
    if (value.length !== 6) return;
    
    // Prevent double-submit using ref (more reliable than state for race conditions)
    if (verifyInProgress.current || loading) {
      console.log('handleVerifyCode - already in progress, skipping');
      return;
    }
    
    verifyInProgress.current = true;
    setLoading(true);
    try {
      interface LoginResult {
        data: {
          token?: string;
          user?: AuthUser;
        };
      }
      
      let result: LoginResult | undefined;
      
      console.log('handleVerifyCode - verifying with method:', authMethod, 'identifier:', authMethod === 'sms' ? phone : email);
      
      if (authMethod === 'email') {
        result = await verifyLoginCode(email, value) as unknown as LoginResult;
      } else if (authMethod === 'sms') {
        result = await verifySmsOtp(phone, value) as unknown as LoginResult;
      } else if (authMethod === 'totp') {
        result = await loginWithTotp(email, value) as unknown as LoginResult;
      }
      
      console.log('handleVerifyCode - result:', result ? 'success' : 'no result');
      
      const token = result?.data?.token;
      const user = result?.data?.user;
      
      if (!token || !user) {
        throw new Error('Invalid response - missing token or user');
      }
      
      login(token, user);
      toast.success(i18n.language === 'sv' ? 'Inloggning lyckades!' : 'Login successful!');
      navigate('/dashboard');
    } catch (error: unknown) {
      console.error('Verify code error:', error);
      
      // Check if it's a network error (CORS/connection issue)
      const axiosError = error as { message?: string; response?: { status?: number; data?: { detail?: string } } };
      if (axiosError.message === 'Network Error') {
        toast.error(i18n.language === 'sv' 
          ? 'Nätverksfel. Kontrollera din internetanslutning och försök igen.' 
          : 'Network error. Check your internet connection and try again.');
      } else if (axiosError.response?.status === 400) {
        // Invalid or expired code
        toast.error(i18n.language === 'sv' 
          ? 'Ogiltig eller utgången kod. Begär en ny kod.' 
          : 'Invalid or expired code. Request a new code.');
      } else if (authMethod === 'totp') {
        toast.error(i18n.language === 'sv' ? 'Ogiltig kod. Kontrollera din autentiseringsapp.' : 'Invalid code. Check your authenticator app.');
      } else {
        toast.error(t('invalid_code'));
      }
      setCode('');
    } finally {
      setLoading(false);
      verifyInProgress.current = false;
    }
  };

  const getCurrentMethod = () => AUTH_METHODS[authMethod];
  const MethodIcon = getCurrentMethod().icon;

  const getInputPlaceholder = () => {
    if (authMethod === 'sms') {
      return i18n.language === 'sv' ? 'Telefonnummer' : 'Phone number';
    }
    return t('email');
  };

  const getCodeDescription = () => {
    if (authMethod === 'email') {
      return i18n.language === 'sv' 
        ? `En kod har skickats till: ${email}`
        : `A code has been sent to: ${email}`;
    } else if (authMethod === 'sms') {
      return i18n.language === 'sv'
        ? `En kod har skickats till: ${phone}`
        : `A code has been sent to: ${phone}`;
    } else {
      return i18n.language === 'sv'
        ? 'Ange koden från din autentiseringsapp'
        : 'Enter the code from your authenticator app';
    }
  };

  return (
    <div className="login-split">
      {/* Image side */}
      <div 
        className="login-image"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/399197/pexels-photo-399197.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="w-full h-full bg-slate-900/20" />
      </div>
      
      {/* Form side */}
      <div className="login-form">
        <div className="w-full max-w-sm">
          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            className="absolute top-4 right-4 flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
            data-testid="login-language-toggle"
          >
            <Globe size={18} />
            {i18n.language === 'sv' ? 'EN' : 'SV'}
          </button>
          
          {/* Logo */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {t('app_name')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {step === 'method' && (i18n.language === 'sv' ? 'Välj inloggningsmetod' : 'Choose login method')}
              {step === 'input' && (authMethod === 'totp' 
                ? (i18n.language === 'sv' ? 'Ange din e-postadress' : 'Enter your email')
                : (i18n.language === 'sv' 
                  ? (authMethod === 'sms' ? 'Ange ditt telefonnummer' : 'Ange din e-postadress')
                  : (authMethod === 'sms' ? 'Enter your phone number' : 'Enter your email'))
              )}
              {step === 'code' && (i18n.language === 'sv' ? 'Ange kod' : 'Enter code')}
            </p>
          </div>

          {/* Method Selection */}
          {step === 'method' && (
            <div className="space-y-3" data-testid="auth-method-selection">
              {Object.values(AUTH_METHODS).map((method) => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.id}
                    onClick={() => selectMethod(method.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all hover:border-slate-400 hover:bg-slate-50 ${
                      authMethod === method.id ? 'border-slate-800 bg-slate-50' : 'border-slate-200'
                    }`}
                    data-testid={`auth-method-${method.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      authMethod === method.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <Icon size={20} />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-medium text-slate-900">
                        {i18n.language === 'sv' ? method.label : method.labelEn}
                      </div>
                      <div className="text-xs text-slate-500">
                        {i18n.language === 'sv' ? method.description : method.descriptionEn}
                      </div>
                    </div>
                  </button>
                );
              })}
              
              {/* DEV Login Section - Only visible when enabled by Superadmin */}
              {devLoginEnabled && (
                <div className="mt-6 pt-6 border-t border-dashed border-amber-300" data-testid="dev-login-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Code size={16} className="text-amber-600" />
                    <span className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                      DEV Login
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {DEV_ROLES.map((devRole) => {
                      const RoleIcon = devRole.icon;
                      return (
                        <button
                          key={devRole.role}
                          onClick={() => handleDevLogin(devRole.role)}
                          disabled={devLoading !== null}
                          className={`flex items-center gap-2 p-2.5 rounded-md text-white text-sm font-medium transition-all ${devRole.color} ${
                            devLoading === devRole.role ? 'opacity-70' : ''
                          }`}
                          data-testid={`dev-login-${devRole.role}`}
                        >
                          {devLoading === devRole.role ? (
                            <Spinner size={16} className="animate-spin" />
                          ) : (
                            <RoleIcon size={16} weight="bold" />
                          )}
                          <span className="truncate">{devRole.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-amber-600 mt-2 text-center">
                    ⚠️ Endast för utveckling/testning
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Input Step */}
          {step === 'input' && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              {/* Method indicator with switcher */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowMethodPicker(!showMethodPicker)}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 mb-3"
                >
                  <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                    <MethodIcon size={14} />
                  </div>
                  <span>{i18n.language === 'sv' ? getCurrentMethod().label : getCurrentMethod().labelEn}</span>
                  <CaretDown size={14} />
                </button>
                
                {showMethodPicker && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                    {Object.values(AUTH_METHODS).map((method) => {
                      const Icon = method.icon;
                      return (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => selectMethod(method.id)}
                          className={`w-full flex items-center gap-3 p-3 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg ${
                            authMethod === method.id ? 'bg-slate-50' : ''
                          }`}
                        >
                          <Icon size={16} className="text-slate-500" />
                          <span className="text-sm">{i18n.language === 'sv' ? method.label : method.labelEn}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Input field(s) */}
              {authMethod === 'sms' ? (
                <div className="relative">
                  <Phone 
                    size={20} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                  />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder={i18n.language === 'sv' ? '070-123 45 67' : 'Phone number'}
                    className="pl-10"
                    autoFocus
                    data-testid="login-phone-input"
                  />
                </div>
              ) : (
                <div className="relative">
                  <MethodIcon 
                    size={20} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('email')}
                    className="pl-10"
                    autoFocus
                    data-testid="login-email-input"
                  />
                </div>
              )}
              
              <Button
                type="submit"
                disabled={(authMethod === 'sms' ? !phone : !email) || loading}
                className="w-full"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Spinner size={18} className="animate-spin" />
                ) : (
                  <>
                    {authMethod === 'totp' 
                      ? (i18n.language === 'sv' ? 'Fortsätt' : 'Continue')
                      : t('send_code')
                    }
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep('method')}
                className="w-full text-sm text-slate-500 hover:text-slate-700 mt-4"
              >
                ← {i18n.language === 'sv' ? 'Byt inloggningsmetod' : 'Change login method'}
              </button>
            </form>
          )}

          {/* Code Entry Step */}
          {step === 'code' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                  <MethodIcon size={14} />
                </div>
                <span>{i18n.language === 'sv' ? getCurrentMethod().label : getCurrentMethod().labelEn}</span>
              </div>
              
              <p className="text-sm text-slate-600">
                {getCodeDescription()}
              </p>
              
              <div>
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(value) => {
                    setCode(value);
                    // Clear any pending verification
                    if (verifyTimeoutRef.current) {
                      clearTimeout(verifyTimeoutRef.current);
                    }
                    if (value.length === 6 && !verifyInProgress.current && !loading) {
                      // Debounce to prevent double-calls from paste events
                      verifyTimeoutRef.current = setTimeout(() => {
                        handleVerifyCode(value);
                      }, 100);
                    }
                  }}
                  data-testid="login-otp-input"
                  className="justify-start"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              {loading && (
                <div className="flex justify-center">
                  <Spinner size={24} className="animate-spin text-slate-400" />
                </div>
              )}
              
              {/* Resend code button (not for TOTP) */}
              {authMethod !== 'totp' && (
                <button
                  onClick={handleRequestCode}
                  disabled={loading}
                  className="w-full text-sm text-slate-500 hover:text-slate-700"
                >
                  {i18n.language === 'sv' ? 'Skicka ny kod' : 'Resend code'}
                </button>
              )}
              
              <button
                onClick={() => {
                  setStep('input');
                  setCode('');
                }}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
                data-testid="login-back-button"
              >
                ← {t('back')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
